"use client";

import { useEffect, useRef, useState } from "react";
import { GREENPAK_FUND_PERCENT } from "@/greenpak/greenpak-fund";

/** Floating, click-to-open badge in the hero's top corner — collapsed to an
 *  icon on load, expands into the same card style as the Commitment toward
 *  Communities notice on tour pages. */
export function GreenPakFundBadge() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click, not just via the X button.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div className="absolute right-4 top-4 z-20 sm:right-7 sm:top-7">
      {open ? (
        <>
          {/* Dims the page on narrow screens, where the card below is nearly
              full-width and would otherwise sit on top of the wordmark/quote. */}
          <div className="fixed inset-0 z-20 bg-black/55 sm:hidden" aria-hidden="true" />
          <div
            ref={containerRef}
            className="fixed left-1/2 top-24 z-20 w-[calc(100vw-2.5rem)] max-w-[320px] -translate-x-1/2 rounded-2xl border border-[color-mix(in_srgb,var(--green)_40%,transparent)] bg-[rgba(7,17,13,0.85)] p-5 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.45)] motion-safe:animate-[pbdRise_0.35s_ease_both] sm:static sm:left-auto sm:top-auto sm:w-[min(320px,calc(100vw-2.5rem))] sm:max-w-none sm:translate-x-0"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--green)_22%,transparent)] text-lg text-[var(--green)]">
                  ♻
                </span>
                <h3 className="m-0 font-[family-name:var(--font-display)] text-[1.05rem] font-medium text-[var(--on-dark)]">
                  Commitment Toward Communities
                </h3>
              </div>
              {/* 44x44 minimum touch target (was 28px) */}
              <button
                aria-label="Close"
                onClick={() => setOpen(false)}
                type="button"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-[rgba(255,255,255,0.15)] text-[rgba(255,247,231,0.7)] cursor-pointer transition-colors duration-150 hover:border-[var(--cta)] hover:text-[var(--on-dark)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--cta)]"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
            <p className="mt-3.5 mb-0 text-[0.85rem] leading-[1.75] text-[rgba(255,247,231,0.85)]">
              Every booking puts <b className="text-[var(--on-dark)]">{GREENPAK_FUND_PERCENT}% of trip value</b>{" "}
              into local conservation and community projects in the valleys you travel, trail
              clean-ups, tree planting, and school support. Your guide will tell you exactly
              where this trip&apos;s contribution goes.
            </p>
          </div>
        </>
      ) : (
        <button
          aria-label="Read about our Commitment Toward Communities"
          aria-expanded="false"
          onClick={() => setOpen(true)}
          type="button"
          className="grid h-11 w-11 place-items-center rounded-full border border-[color-mix(in_srgb,var(--green)_45%,transparent)] bg-[rgba(7,17,13,0.55)] text-xl text-[var(--green)] backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.35)] cursor-pointer transition duration-150 hover:-translate-y-0.5 hover:bg-[rgba(7,17,13,0.75)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--cta)]"
        >
          ♻
        </button>
      )}
    </div>
  );
}
